import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { setSelectedPatient } from "@store/homeSlice";
import { useUpdatePatientMutation } from "@store/patientApi";

/**
 * Player-side toggle for the in-game voice-over avatar overlay (Phase 1).
 *
 * Reads the persisted preference from
 * `state.home.selectedPatient.gameVoiceOverPrefs[gameId]` (server source of
 * truth) and exposes a setter that PATCHes only the requested key. The
 * backend shallow-merges the partial map so other games' values are never
 * disturbed.
 *
 * Returns `false` while no patient is selected so the overlay never mounts
 * before settings are available.
 */
export function useVoiceOverPref(gameId: string): [boolean, (next: boolean) => void] {
  const dispatch = useDispatch();
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const selectedPatient = useSelector(
    (state: RootState) => state.home.selectedPatient,
  );
  const [updatePatient] = useUpdatePatientMutation();

  const enabled =
    !!selectedPatient?.gameVoiceOverPrefs &&
    selectedPatient.gameVoiceOverPrefs[gameId] === true;

  const setEnabled = useCallback(
    (next: boolean) => {
      if (!selectedPatientId || !selectedPatient) return;
      // Optimistic local update so the toggle button reacts instantly even
      // before the PATCH round-trips. The mutation result will replace this
      // with the canonical server response.
      dispatch(
        setSelectedPatient({
          ...selectedPatient,
          gameVoiceOverPrefs: {
            ...(selectedPatient.gameVoiceOverPrefs ?? {}),
            [gameId]: next,
          },
        }),
      );
      updatePatient({
        id: selectedPatientId,
        gameVoiceOverPrefs: { [gameId]: next },
      })
        .unwrap()
        .then((updated) => {
          dispatch(setSelectedPatient(updated));
        })
        .catch(() => {
          // Revert on failure so the UI matches the server again.
          dispatch(setSelectedPatient(selectedPatient));
        });
    },
    [selectedPatientId, selectedPatient, updatePatient, dispatch, gameId],
  );

  return [enabled, setEnabled];
}
